import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { regionApi } from "@/lib/api/admin-api";
import { companyApi } from "@/lib/api/services/company.api";
import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@/components/ui/skeleton";
import { DonutStatCard } from "@/components/common/DonutStatCard";

export function RegionStats() {
  const { t } = useLingui();
  const { isAuthenticated, hasPermission, user } = useAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["regions", "stats"],
    queryFn: regionApi.getStats,
    enabled: isAuthenticated,
  });

  const canReadCompanies = hasPermission("company:read");
  const organizationId = user?.organization?.id;

  const { data: companiesStats } = useQuery({
    queryKey: ["companies", "stats", organizationId ?? "all"],
    queryFn: () =>
      organizationId
        ? companyApi.getScopedStats({ organizationId })
        : companyApi.getGlobalStats(),
    enabled: isAuthenticated && canReadCompanies,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-2 rounded-xl bg-(--color-surface-muted) p-2 sm:grid-cols-2 xl:grid-cols-4">
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 rounded-xl bg-(--color-surface-muted) p-2 sm:grid-cols-2 xl:grid-cols-4">
      <DonutStatCard
        title={t`Regions`}
        total={stats?.regions?.total ?? 0}
        active={stats?.regions?.active ?? 0}
        inactive={stats?.regions?.inactive ?? 0}
      />
      <DonutStatCard
        title={t`Locations (Restaurants)`}
        total={stats?.locations?.total ?? 0}
        active={stats?.locations?.active ?? 0}
        inactive={stats?.locations?.inactive ?? 0}
      />
      <DonutStatCard
        title={t`Users`}
        total={stats?.users?.total ?? 0}
        active={stats?.users?.active ?? 0}
        inactive={stats?.users?.inactive ?? 0}
      />
      <DonutStatCard
        title={t`Concepts`}
        total={companiesStats?.total ?? 0}
        active={companiesStats?.active ?? 0}
        inactive={companiesStats?.inactive ?? 0}
      />
    </div>
  );
}
